import { dataKey, getStocks, type OrderStats, type Product } from "./product-types";

export const TURNOVER_ORDER_DAYS = 7;

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function sumLast(values: number[] | undefined, days: number): number {
  if (!values?.length) return 0;
  return values.slice(-days).reduce((sum, value) => sum + (finiteNumber(value) ?? 0), 0);
}

export function getTurnoverOrderQty(
  product: Product,
  orderData: Record<string, OrderStats>,
  days = TURNOVER_ORDER_DAYS,
): number {
  const stats = orderData[dataKey(product)];
  return getTurnoverOrderQtyFromStats(stats, days);
}

export function getTurnoverOrderQtyFromStats(
  stats: OrderStats | null | undefined,
  days = TURNOVER_ORDER_DAYS,
): number {
  if (!stats) return 0;
  if (days === 7) return finiteNumber(stats.qty_7d) ?? sumLast(stats.daily_orders_30d, days);
  if (days === 30) return finiteNumber(stats.qty_30d) ?? sumLast(stats.daily_orders_30d, days);
  return sumLast(stats.daily_orders_30d, days);
}

export function getTurnoverSalesPerDay(
  product: Product,
  orderData: Record<string, OrderStats>,
  days = TURNOVER_ORDER_DAYS,
): number {
  return getTurnoverOrderQty(product, orderData, days) / days;
}

export function getTurnoverSalesPerDayFromStats(
  stats: OrderStats | null | undefined,
  days = TURNOVER_ORDER_DAYS,
): number {
  return getTurnoverOrderQtyFromStats(stats, days) / days;
}

export function getTurnoverDaysToZero(
  product: Product,
  orderData: Record<string, OrderStats>,
  days = TURNOVER_ORDER_DAYS,
): number | null {
  const speed = getTurnoverSalesPerDay(product, orderData, days);
  if (speed <= 0) return null;
  return getStocks(product).present / speed;
}

export function getTurnoverDaysToZeroFromStats(
  product: Product,
  stats: OrderStats | null | undefined,
  days = TURNOVER_ORDER_DAYS,
): number | null {
  const speed = getTurnoverSalesPerDayFromStats(stats, days);
  if (speed <= 0) return null;
  return getStocks(product).present / speed;
}

export function getTurnoverSummary(
  products: Product[],
  orderData: Record<string, OrderStats>,
  days = TURNOVER_ORDER_DAYS,
): { stock: number; orderQty: number; salesPerDay: number; daysToZero: number | null } {
  const stock = products.reduce((sum, product) => sum + getStocks(product).present, 0);
  const orderQty = products.reduce((sum, product) => sum + getTurnoverOrderQty(product, orderData, days), 0);
  const salesPerDay = orderQty / days;
  return {
    stock,
    orderQty,
    salesPerDay,
    daysToZero: salesPerDay > 0 ? stock / salesPerDay : null,
  };
}
